import React, { useState } from 'react';
import { LuSearch, LuX } from 'react-icons/lu';

import { Account } from '@ndvr/portfolio-api';
import { AggregatedTransactionReportView } from '@ndvr/reporting-api';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/utils/cn';
import { pluralize } from '@/utils/string-utils';
import { formatDate } from 'utils';

import { formatFullCurrency } from '../../reporting/format';
import {
    gainCell,
    HEAD_CLASS,
    HEAD_ROW_CLASS,
    RIGHT_HEAD_CLASS,
    TABLE_CARD_HEADER_CLASS,
    TABLE_CLASS,
    TABLE_FOOTER_CLASS,
} from '../../reporting/table-styles';

// The inline ledger shows only the latest transactions in range; "View all" opens the rest.
const MAX_ROWS = 10;
// All Time on a large household can cover thousands of rows, so the dialog renders only the most
// recent page of what matches (as the old Transactions tab did) and the search narrows the rest.
const DIALOG_MAX_ROWS = 100;

interface LedgerTableProps {
    rows: AggregatedTransactionReportView[];
    accountName: (accountGUID: string) => string;
    // Shown in place of the rows when there are none.
    emptyText: string;
    footerLeft: string;
    // Classes for the wrapper around the table (not the footer), e.g. the dialog's scroll area.
    className?: string;
}

// The ledger table and its footer, shared by the card and the "View all" dialog (HoldingsTable's split).
const LedgerTable = ({ rows, accountName, emptyText, footerLeft, className }: LedgerTableProps) => (
    <>
        <div className={className}>
            <Table className={cn(TABLE_CLASS, 'min-w-[720px]')}>
                <TableHeader>
                    <TableRow className={HEAD_ROW_CLASS}>
                        <TableHead className={HEAD_CLASS}>Date</TableHead>
                        <TableHead className={HEAD_CLASS}>Account</TableHead>
                        <TableHead className={HEAD_CLASS}>Type</TableHead>
                        <TableHead className={HEAD_CLASS}>Description</TableHead>
                        <TableHead className={RIGHT_HEAD_CLASS}>Amount</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.length > 0 ? (
                        rows.map((row) => (
                            // Transaction ids come from the custodians, so they are only unique
                            // within a portfolio — the aggregated ledger spans several.
                            <TableRow key={`${row.portfolioGUID}-${row.transactionId}`}>
                                <TableCell className="text-cocoa-700 text-sm whitespace-nowrap">
                                    {formatDate(row.date, 'MMM d, yyyy')}
                                </TableCell>
                                <TableCell className="text-cocoa-900 text-sm">
                                    {accountName(row.portfolioGUID)}
                                </TableCell>
                                <TableCell className="text-cocoa-900 text-sm whitespace-nowrap">
                                    {row.transactionType}
                                </TableCell>
                                <TableCell>
                                    <div className="text-cocoa-900 text-sm">{row.transactionDetail}</div>
                                    {row.transferAccount ? (
                                        <div className="text-cocoa-400 text-xs">{row.transferAccount}</div>
                                    ) : null}
                                </TableCell>
                                <TableCell className={gainCell(row.amount)}>{formatFullCurrency(row.amount)}</TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={5} className="text-cocoa-400 py-6 text-center text-sm">
                                {emptyText}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
        <div className={TABLE_FOOTER_CLASS}>{footerLeft}</div>
    </>
);

interface AllTransactionsDialogBodyProps {
    rows: AggregatedTransactionReportView[];
    accountName: (accountGUID: string) => string;
}

// The "View all" dialog's contents, as AllHoldingsDialogBody. Its own component so the search
// state lives inside the dialog: Radix unmounts the content on close, so every open starts with
// an empty search.
const AllTransactionsDialogBody = ({ rows, accountName }: AllTransactionsDialogBodyProps) => {
    const [search, setSearch] = useState('');
    const query = search.trim().toLowerCase();
    const matchingRows = query
        ? rows.filter((row) =>
              [row.transactionType, row.transactionDetail, accountName(row.portfolioGUID)].some((text) =>
                  text?.toLowerCase().includes(query),
              ),
          )
        : rows;
    const visibleRows = matchingRows.slice(0, DIALOG_MAX_ROWS);

    return (
        <>
            <DialogHeader className="flex-row flex-wrap items-center gap-3 space-y-0 border-b px-6 py-5">
                <DialogTitle className="text-cocoa-1000 flex-1 text-xl">All transactions</DialogTitle>
                <InputGroup className="border-cocoa-200 h-9 w-70 rounded-lg bg-white">
                    <InputGroupAddon align="inline-start">
                        <LuSearch className="text-cocoa-400 h-4 w-4" />
                    </InputGroupAddon>
                    <InputGroupInput
                        type="search"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Search type, description or account"
                        aria-label="Search type, description or account"
                        className="placeholder:text-cocoa-400"
                    />
                </InputGroup>
                {/* Design's close button: 36px square like the search field, 8px radius, tint on hover. */}
                <DialogClose asChild>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="text-cocoa-700 hover:bg-cocoa-100 h-9 w-9 rounded-lg"
                    >
                        <LuX className="h-5 w-5" />
                        <span className="sr-only">Close</span>
                    </Button>
                </DialogClose>
            </DialogHeader>
            {/* min-h-0 lets the table's wrapper shrink below its content so it scrolls instead of
                growing the fixed-height dialog. */}
            <LedgerTable
                rows={visibleRows}
                accountName={accountName}
                emptyText={`No transactions match “${search.trim()}”.`}
                footerLeft={
                    visibleRows.length < matchingRows.length
                        ? `Showing the ${DIALOG_MAX_ROWS} most recent of ${matchingRows.length}${query ? ' matching' : ''} transactions`
                        : query
                          ? `Showing ${matchingRows.length} of ${pluralize(rows.length, 'transaction')}`
                          : pluralize(rows.length, 'transaction')
                }
                className="min-h-0 flex-1 overflow-auto"
            />
        </>
    );
};

interface TransactionsTableProps {
    transactions?: AggregatedTransactionReportView[];
    // The household's accounts, to name the account each transaction belongs to.
    accounts: Account[];
    isLoading?: boolean;
}

// Card shell, header, rows and footer follow PlannedWithdrawalsCard (the shared reporting table
// styles); the "View all" button and dialog follow HoldingsBreakdownTable.
export const TransactionsTable = ({ transactions = [], accounts, isLoading }: TransactionsTableProps) => {
    const [isAllOpen, setIsAllOpen] = useState(false);

    if (isLoading) {
        return <div className="bg-cocoa-100 h-64 w-full animate-pulse rounded-2xl" />;
    }

    // Newest first, once, for both the card and the dialog. Dates arrive as "yyyy-MM-dd'Z'", so
    // the fixed-width prefix sorts chronologically as a plain string comparison.
    const rows = [...transactions].sort((a, b) => b.date.localeCompare(a.date));
    // A transaction's portfolioGUID is the GUID of the account it was booked in. Looked up once per
    // row on every search keystroke, so it reads from a map rather than scanning the accounts.
    const nameByAccountGUID = new Map(
        accounts.map((account) => [account.accountGUID, account.accountName.plainText] as const),
    );
    const accountName = (accountGUID: string) => nameByAccountGUID.get(accountGUID) ?? 'Unknown account';

    return (
        <Card className="border-border overflow-hidden rounded-2xl border bg-white">
            <div className={cn(TABLE_CARD_HEADER_CLASS, 'justify-between')}>
                <h3 className="font-heading text-cocoa-1000 text-xl">Transaction history</h3>
                {/* HoldingsBreakdownTable's "View all" link button. */}
                {rows.length > MAX_ROWS ? (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsAllOpen(true)}
                        className="text-seafoam-600 hover:bg-seafoam-0 rounded-lg px-3.5 font-semibold"
                    >
                        View all {rows.length} transactions
                    </Button>
                ) : null}
            </div>
            <LedgerTable
                rows={rows.slice(0, MAX_ROWS)}
                accountName={accountName}
                emptyText="No transactions to display."
                footerLeft={
                    rows.length > MAX_ROWS
                        ? `Showing the ${MAX_ROWS} most recent of ${pluralize(rows.length, 'transaction')}`
                        : pluralize(rows.length, 'transaction')
                }
            />

            <Dialog open={isAllOpen} onOpenChange={setIsAllOpen}>
                {/* Fixed height (not max-h) so filtering the list doesn't shrink the dialog. */}
                <DialogContent showCloseButton={false} className="flex h-[85vh] flex-col gap-0 p-0 sm:max-w-6xl">
                    <AllTransactionsDialogBody rows={rows} accountName={accountName} />
                </DialogContent>
            </Dialog>
        </Card>
    );
};
